import { useState } from 'react';
import { Music, Play, Pause, Clock, Trash2, AlertTriangle, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getStatusColor, getStatusIcon } from '../utils/uploadValidation';

export function MusicTable({ music, loading, onPlayMusic, currentTrack, isPlaying, onDeleteMusic }) {
  const [itemToDelete, setItemToDelete] = useState(null);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading music...</div>;
  }

  if (music.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Music size={48} className="mx-auto mb-3 opacity-30" />
        <p className="text-gray-500">No music uploaded yet</p>
      </div>
    );
  }

  const confirmDelete = () => {
    if (itemToDelete && onDeleteMusic) {
      onDeleteMusic(itemToDelete.key);
      setItemToDelete(null);
    }
  };

  return (
    <>
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Title</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Artist</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Album</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
              {/* Added Header Column */}
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Feedback</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Uploaded</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {music.map((item, idx) => {
              const isCurrentTrack = currentTrack?.key === item.key;
              return (
                <motion.tr 
                  key={item.key} 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="hover:bg-gray-50 transition"
                >
                  <td className="px-6 py-4">
                    <p className="font-medium text-gray-900 truncate">{item.name}</p>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{item.artist}</td>
                  <td className="px-6 py-4 text-gray-600">{item.album}</td>
                  <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(item.status)}`}>
                  {getStatusIcon(item.status)} {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </span>
                  </td>

                  {/* Added Feedback Column Logic */}
                  <td className="px-6 py-4">
                    {item.status === 'rejected' && item.rejectionReason ? (
                      <div className="flex items-start gap-1.5 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100 min-w-[150px]">
                        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                        <span className="leading-tight">{item.rejectionReason}</span>
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs italic">No feedback</span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                    <Clock size={16} />
                    {new Date(item.uploadedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })}
                    </div>
                </td>
                  
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center gap-3">
                        <button
                            onClick={() => onPlayMusic(item.url, item)}
                            disabled={item.status !== 'approved'}
                            className={`transition-colors ${
                                item.status === 'approved' 
                                  ? 'text-purple-600 hover:text-purple-800' 
                                  : 'text-gray-300 cursor-not-allowed'
                            }`}
                        >
                            {isCurrentTrack && isPlaying ? <Pause size={18} /> : <Play size={18} />}
                        </button>
                        <button
                            onClick={() => setItemToDelete(item)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal remains the same ... */}
      <AnimatePresence>
        {itemToDelete && (
          /* ... modal content from previous snippet ... */
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
             {/* ... motion components ... */}
             <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4 text-red-600">
                  <div className="p-3 bg-red-100 rounded-full">
                    <AlertTriangle size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                </div>
                <p className="text-gray-600 mb-6">Are you sure you want to delete <span className="font-semibold text-gray-900">"{itemToDelete.name}"</span>?</p>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setItemToDelete(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition">Cancel</button>
                  <button onClick={confirmDelete} className="px-6 py-2 bg-red-600 text-white rounded-lg transition">Delete Track</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}